import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AdminFormPage } from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { useV2GetProjectAttachmentByIdQuery } from "@generated";
import { ProjectAttachmentForm } from "../ProjectAttachmentForm/ProjectAttachmentForm";
import { useProjectAttachmentMutations } from "../api/useProjectAttachmentMutations";
import { useAttachmentTypeOptions } from "../api/useAttachmentTypeOptions";
import { useDownloadAttachment } from "../api/useDownloadAttachment";
import type { ProjectAttachmentFormValues } from "../types";

const FORM_ID = "project-attachment-form";

export interface EditProps {
  projectId: string;
}

const Edit = ({ projectId }: EditProps): JSX.Element => {
  const navigate = useNavigate();
  const { id = "" } = useParams();
  const { update, uploadFile } = useProjectAttachmentMutations();
  const { options } = useAttachmentTypeOptions();
  const download = useDownloadAttachment();
  const { data, isLoading, refetch } = useV2GetProjectAttachmentByIdQuery(
    { id: id, include: ["attachment-type"] },
    { refetchOnMountOrArgChange: true }
  );
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<{
    variant: "success" | "danger";
    text: string;
  } | null>(null);

  const attrs = (data?.data?.attributes as Record<string, any>) ?? {};
  const currentFilename: string = attrs.document ?? "";
  const currentTypeId = (data?.data as any)?.relationships?.["attachment-type"]
    ?.data?.id;
  const initialValues = useMemo<ProjectAttachmentFormValues>(
    () => ({
      attachmentTypeId: currentTypeId != null ? String(currentTypeId) : "",
      file: null,
    }),
    [currentTypeId]
  );

  if (isLoading) return <ScopeStackSpinner />;

  const handleSubmit = async (values: ProjectAttachmentFormValues) => {
    setSubmitting(true);
    try {
      await update(
        { attachmentTypeId: values.attachmentTypeId },
        { projectId, id }
      );
      if (values.file) await uploadFile(id, values.file);
      // Generated API has no cache tags; refetch so the GET cache is fresh.
      refetch();
      setToast({ variant: "success", text: "Attachment saved" });
      setSubmitting(false);
    } catch (err: any) {
      const detail = err?.data?.errors?.[0]?.detail;
      const isDup =
        typeof detail === "string" && /attachment type/i.test(detail);
      setToast({
        variant: "danger",
        text: isDup
          ? "An attachment of this type already exists for this project."
          : "Could not save the attachment.",
      });
      setSubmitting(false);
    }
  };

  return (
    <>
      <AdminFormPage
        title="Edit Attachment"
        formId={FORM_ID}
        onCancel={() => navigate("../..", { relative: "path" })}
        isSubmitting={submitting}
      >
        <ProjectAttachmentForm
          id={FORM_ID}
          mode="edit"
          initialValues={initialValues}
          typeOptions={options}
          currentFilename={currentFilename}
          onDownload={
            currentFilename
              ? () => {
                  download(id, currentFilename).catch(() =>
                    setToast({
                      variant: "danger",
                      text: "Could not download the attachment.",
                    })
                  );
                }
              : undefined
          }
          onSubmit={handleSubmit}
        />
      </AdminFormPage>
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default Edit;
